import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ROUTES } from "@/config/routes";
import {
  STEPS,
  type DocumentKey,
} from "@/containers/public/onboarding/constants";
import {
  createInitialData,
  validateStep,
} from "@/containers/public/onboarding/helpers";
import type {
  FormData,
  OnboardingCatalogs,
} from "@/containers/public/onboarding/types";
import { usePortalContext } from "@/hooks/usePortalContext";
import { useTRPC } from "@/trpc/client";
import { getRequestPayload, toFormData, toUploadedDocs } from "./helpers";

// Marks the form as filled from the template, so neither effect hydrates it
// again before the request exists.
const TEMPLATE_HYDRATION = "template";

const EMPTY_CATALOGS: OnboardingCatalogs = { cities: [], erps: [], banks: [] };

const errorMessage = (cause: unknown, fallback: string): string =>
  cause instanceof Error ? cause.message : fallback;

/**
 * The org-facing sibling of `useOnboarding`: same steps and rules, deliberately
 * not the same module. There is no localStorage draft, no `?id=` resume link
 * and no email modal here — the request id lives in the route and the user is
 * already authenticated. A new request starts from the organization's template.
 */
export function useMerchantRequest() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: routeId } = useParams<{ id: string }>();
  const { context } = usePortalContext();
  const orgId = context?.type === "org" ? context.id : undefined;

  const [requestId, setRequestId] = useState<string | undefined>(routeId);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<FormData>(createInitialData);
  const [uploadedDocs, setUploadedDocs] = useState<
    Partial<Record<DocumentKey, string>>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoObjectUrl, setLogoObjectUrl] = useState<string>();
  const hydratedIdRef = useRef<string>();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const catalogsQuery = useQuery({
    ...trpc.onboarding.catalogs.queryOptions(undefined),
    staleTime: Infinity,
  });

  const requestQuery = useQuery({
    ...trpc.merchantRequests.get.queryOptions({
      rentalsOrgId: orgId ?? "",
      id: requestId ?? "",
    }),
    enabled: !!orgId && !!requestId,
  });

  const templateQuery = useQuery({
    ...trpc.merchantRequests.template.queryOptions({
      rentalsOrgId: orgId ?? "",
    }),
    enabled: !!orgId && !requestId,
  });

  const logoUrlQuery = useQuery({
    ...trpc.merchantRequests.getDocumentUrl.queryOptions({
      rentalsOrgId: orgId ?? "",
      id: requestId ?? "",
      documentKey: "logo",
    }),
    // A fresh upload previews from its own object URL.
    enabled: !!orgId && !!requestId && !!uploadedDocs.logo && !logoObjectUrl,
  });

  const request = requestQuery.data;
  const template = templateQuery.data;

  // Hydrate once per request id. Re-hydrating on every refetch would throw away
  // whatever the user has typed since the last save.
  useEffect(() => {
    if (!request || hydratedIdRef.current === request.id) return;
    hydratedIdRef.current = request.id;
    setData(toFormData(request));
    setUploadedDocs(toUploadedDocs(request.documents));
  }, [request]);

  // A new request starts from the template's defaults, once.
  useEffect(() => {
    if (!template || hydratedIdRef.current) return;
    hydratedIdRef.current = TEMPLATE_HYDRATION;
    setData(toFormData(template));
  }, [template]);

  useEffect(() => {
    return () => {
      if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
    };
  }, [logoObjectUrl]);

  const createRequest = useMutation(
    trpc.merchantRequests.create.mutationOptions()
  );
  const saveDraft = useMutation(
    trpc.merchantRequests.saveDraft.mutationOptions()
  );
  const submitRequest = useMutation(
    trpc.merchantRequests.submit.mutationOptions()
  );
  const getDocumentUploadUrl = useMutation(
    trpc.merchantRequests.getDocumentUploadUrl.mutationOptions()
  );
  const confirmDocumentUpload = useMutation(
    trpc.merchantRequests.confirmDocumentUpload.mutationOptions()
  );

  const invalidateList = () => {
    if (!orgId) return;
    void queryClient.invalidateQueries({
      queryKey: trpc.merchantRequests.list.queryKey({ rentalsOrgId: orgId }),
    });
  };

  const updateData = (field: string, value: string | boolean) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const showFieldErrors = (nextErrors: Record<string, string>) => {
    setFieldErrors(nextErrors);
    requestAnimationFrame(() => {
      document
        .querySelector('[data-onboarding-error="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  /** Create on the first step, update on every later one. */
  const persist = async (): Promise<boolean> => {
    if (!orgId) return false;

    setIsSaving(true);
    setError(undefined);
    try {
      const fields = getRequestPayload(data);

      if (!requestId) {
        const created = await createRequest.mutateAsync({
          rentalsOrgId: orgId,
          ...fields,
        });
        // Claim the id before the route change so the load effect does not
        // re-hydrate over the form the user is still filling.
        hydratedIdRef.current = created.id;
        // create may copy the organization's logo into documents.logo; the
        // hydration effect is skipped for this id, so seed the uploaded
        // documents here.
        setUploadedDocs(toUploadedDocs(created.documents));
        setRequestId(created.id);
        navigate(
          ROUTES.DASHBOARD.MERCHANT_REQUEST_DETAIL.replace(":id", created.id),
          { replace: true }
        );
      } else {
        await saveDraft.mutateAsync({
          rentalsOrgId: orgId,
          id: requestId,
          ...fields,
        });
      }

      invalidateList();
      return true;
    } catch (cause) {
      setError(errorMessage(cause, "No se pudo guardar la información."));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = async () => {
    const nextErrors = validateStep(currentStep, data, uploadedDocs);
    if (Object.keys(nextErrors).length > 0) {
      showFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    if (!(await persist())) return;
    if (currentStep < STEPS.length) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    const nextErrors = validateStep(STEPS.length, data, uploadedDocs);
    if (Object.keys(nextErrors).length > 0) {
      showFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});
    if (!orgId || !requestId) return;

    setIsSubmitting(true);
    setError(undefined);
    try {
      await saveDraft.mutateAsync({
        rentalsOrgId: orgId,
        id: requestId,
        ...getRequestPayload(data),
      });
      await submitRequest.mutateAsync({ rentalsOrgId: orgId, id: requestId });
      invalidateList();
      navigate(ROUTES.DASHBOARD.MERCHANTS);
    } catch (cause) {
      setError(errorMessage(cause, "No se pudo enviar la solicitud."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadDocument = async (
    documentKey: DocumentKey,
    file: File
  ): Promise<boolean> => {
    if (!orgId || !requestId) {
      setError("Guarda la identidad del comercio antes de cargar documentos.");
      return false;
    }

    setIsUploading(true);
    setError(undefined);
    try {
      const { uploadUrl } = await getDocumentUploadUrl.mutateAsync({
        rentalsOrgId: orgId,
        id: requestId,
        documentKey,
      });
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!response.ok) throw new Error("No se pudo cargar el archivo.");

      await confirmDocumentUpload.mutateAsync({
        rentalsOrgId: orgId,
        id: requestId,
        documentKey,
        fileName: file.name,
      });
      setUploadedDocs((prev) => ({ ...prev, [documentKey]: file.name }));
      setFieldErrors((prev) => {
        if (!prev[documentKey]) return prev;
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      return true;
    } catch (cause) {
      setError(errorMessage(cause, "No se pudo cargar el archivo."));
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (documentKey: DocumentKey) => {
    fileInputRefs.current[documentKey]?.click();
  };

  const handleFileChange = async (
    documentKey: DocumentKey,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (
      file &&
      (await uploadDocument(documentKey, file)) &&
      documentKey === "logo"
    )
      setLogoObjectUrl(URL.createObjectURL(file));
  };

  const status = request?.status ?? "draft";

  return {
    currentStep,
    steps: STEPS,
    data,
    uploadedDocs,
    logoPreviewUrl: logoObjectUrl ?? logoUrlQuery.data?.url,
    fieldErrors,
    error,
    isLoadError: requestId ? requestQuery.isError : templateQuery.isError,
    loadErrorCode: (requestId ? requestQuery : templateQuery).error?.data?.code,
    refetch: () =>
      void (requestId ? requestQuery.refetch() : templateQuery.refetch()),
    catalogs: catalogsQuery.data ?? EMPTY_CATALOGS,
    status,
    isEditable: status === "draft",
    isReturned: status === "draft" && Boolean(request?.customerFacingSummary),
    customerFacingSummary: request?.customerFacingSummary,
    isLoading:
      catalogsQuery.isPending ||
      (requestId ? requestQuery.isPending : templateQuery.isPending),
    isSaving,
    isUploading,
    isSubmitting,
    fileInputRefs,
    updateData,
    handleContinue,
    prevStep,
    handleSubmit,
    handleFileSelect,
    handleFileChange,
  };
}
